import { apiCall } from './api';
import type {
  Team,
  CreateSinglesTeamRequest,
  CreatePairTeamRequest,
  PartnerSearchResult,
  TransferCaptaincyRequest,
} from '@sulis-liiga/common';
import { API_ROUTES } from '@sulis-liiga/common';

/** A team member resolved to their registry profile (for displaying names). */
export interface TeamPlayerProfile {
  id: string;
  firstName: string;
  lastName: string;
  gender?: string;
  profilePicture?: string;
}

const teamService = {
  /**
   * Create a singles team for the authenticated user.
   * An optional custom name may be supplied; when omitted the team is named
   * after the user's first name server-side.
   */
  createSinglesTeam: async (req: CreateSinglesTeamRequest = {}): Promise<Team> => {
    return apiCall<Team>('post', API_ROUTES.TEAM.SINGLES, req);
  },

  /**
   * Create a doubles/mixed-doubles pair team.
   */
  createPairTeam: async (req: CreatePairTeamRequest): Promise<Team> => {
    return apiCall<Team>('post', API_ROUTES.TEAM.PAIR, req);
  },

  /**
   * Search for registered players to add as a partner.
   * For Mixed Doubles, pass format so the server filters by opposite gender.
   */
  searchPartners: async (q: string, format?: string): Promise<PartnerSearchResult[]> => {
    const params = new URLSearchParams({ q });
    if (format) params.set('format', format);
    return apiCall<PartnerSearchResult[]>('get', `${API_ROUTES.TEAM.PARTNER_SEARCH}?${params}`);
  },

  /**
   * Fetch partnership requests sent to the authenticated user (awaiting their acceptance).
   */
  getPartnerRequests: async (): Promise<Team[]> => {
    return apiCall<Team[]>('get', API_ROUTES.TEAM.PARTNER_REQUESTS);
  },

  /**
   * Accept a partner request (the requesting team transitions to active).
   */
  acceptPartnerRequest: async (teamId: string): Promise<Team> => {
    return apiCall<Team>('put', API_ROUTES.TEAM.PARTNER_ACCEPT(teamId));
  },

  /**
   * Decline a partner request (the requesting team is cancelled).
   */
  declinePartnerRequest: async (teamId: string): Promise<Team> => {
    return apiCall<Team>('put', API_ROUTES.TEAM.PARTNER_DECLINE(teamId));
  },

  /**
   * Get the authenticated user's own teams.
   */
  getMyTeams: async (): Promise<Team[]> => {
    return apiCall<Team[]>('get', API_ROUTES.TEAM.MY_TEAMS);
  },

  /**
   * Withdraw a team (creator only).
   */
  withdrawTeam: async (teamId: string): Promise<Team> => {
    return apiCall<Team>('delete', API_ROUTES.TEAM.DELETE(teamId));
  },

  /**
   * Permanently delete a team (creator or Super Admin). A team with no history is
   * removed entirely (`mode: 'hard'`); one with games/enrollments is archived with
   * its historical name preserved (`mode: 'soft'`). Distinct from withdrawTeam.
   */
  deleteTeam: async (teamId: string): Promise<{ id: string; mode: 'hard' | 'soft' }> => {
    return apiCall<{ id: string; mode: 'hard' | 'soft' }>(
      'delete',
      API_ROUTES.TEAM.DELETE_PERMANENT(teamId),
    );
  },

  /**
   * Rename a team.
   */
  renameTeam: async (teamId: string, name: string): Promise<Team> => {
    return apiCall<Team>('patch', API_ROUTES.TEAM.RENAME(teamId), { name });
  },

  /**
   * Get all teams (admin / public listing).
   */
  getAllTeams: async (divisionId?: string): Promise<Team[]> => {
    const url = divisionId
      ? `${API_ROUTES.TEAM.LIST}?divisionId=${divisionId}`
      : API_ROUTES.TEAM.LIST;
    return apiCall<Team[]>('get', url);
  },

  /**
   * Get team by ID.
   */
  getTeamById: async (teamId: string): Promise<Team> => {
    return apiCall<Team>('get', API_ROUTES.TEAM.DETAILS(teamId));
  },

  /**
   * Get a team together with its players resolved to registry profiles (id +
   * name). Members are stored on the team only as user ids; this endpoint
   * resolves them from the player registry so the UI can show real names.
   */
  getTeamWithPlayers: async (
    teamId: string,
  ): Promise<{ team: Team; players: TeamPlayerProfile[] }> => {
    return apiCall<{ team: Team; players: TeamPlayerProfile[] }>(
      'get',
      `${API_ROUTES.TEAM.DETAILS(teamId)}/players`,
    );
  },

  /**
   * Transfer captaincy to another accepted member of the team.
   * Only the current captain (or a League/Super Admin) may do this.
   */
  transferCaptaincy: async (teamId: string, newCaptainUserId: string): Promise<Team> => {
    const req: TransferCaptaincyRequest = { newCaptainUserId };
    return apiCall<Team>('put', API_ROUTES.TEAM.TRANSFER_CAPTAIN(teamId), req);
  },

  /**
   * Get teams for a specific league (public listing).
   */
  getLeagueTeams: async (
    leagueId: string,
    params?: { category?: string; divisionId?: string; q?: string },
  ): Promise<Team[]> => {
    const urlParams = new URLSearchParams({ leagueId });
    if (params?.category) urlParams.set('category', params.category);
    if (params?.divisionId) urlParams.set('divisionId', params.divisionId);
    if (params?.q) urlParams.set('q', params.q);
    return apiCall<Team[]>('get', `${API_ROUTES.TEAM.LIST}?${urlParams}`);
  },
};

export default teamService;
